<script setup lang="ts">
const props = defineProps({
    currentIndex: {
        type: Number,
        required: true
    },
    perPage: {
        type: Number,
        required: true
    },
    total: {
        type: Number,
        default: 0
    },
    paginatedUrl: {
        type: String,
        required: true
    }
})

const maxPage = computed<number>(() =>
    props.total % props.perPage === 0
        ? props.total / props.perPage
        : (props.total - (props.total % props.perPage)) / props.perPage + 1
)

const pagesBefore = computed<number[]>(() => {
    const result = []
    for (let i = 1; i > 0; i -= 1) {
        const prevValue = props.currentIndex - i

        if (prevValue > 0) {
            result.push(prevValue)
        }
    }

    return result
})

const pagesAfter = computed<number[]>(() => {
    const result = []
    for (let i = 1; i <= 1; i += 1) {
        const nextValue = props.currentIndex + i

        if (nextValue < maxPage.value) {
            result.push(nextValue)
        }
    }

    return result
})

const replacePage = (pageIndex: number) => {
    if (pageIndex <= 1) {
        return props.paginatedUrl.replace('/{page}', '')
    }

    if (pageIndex >= maxPage.value) {
        return props.paginatedUrl.replace(`{page}`, `${maxPage.value}`)
    }

    return props.paginatedUrl.replace('{page}', pageIndex.toString())
}
</script>

<template>
    <div class="pagination">
        <NuxtLink
            v-if="currentIndex > 1"
            rel="prev"
            :to="replacePage(currentIndex - 1)"
            :title="$t('word.previous')"
        >
            <button
                type="button"
                class="pagination--prev"
                :disabled="currentIndex === 1"
            >
                <IconChevronLeft is-solid />
            </button>
        </NuxtLink>

        <NuxtLink
            v-show="currentIndex > 1"
            :to="replacePage(1)"
            :title="$t('word.previous')"
        >
            <button
                type="button"
                class="pagination--page pagination--first-page"
            >
                1
            </button>
        </NuxtLink>

        <div v-show="currentIndex > 3" class="pagination--page__hidden button">
            …
        </div>

        <NuxtLink
            v-for="pageBefore in pagesBefore"
            v-show="currentIndex > 2"
            :key="pageBefore"
            :to="replacePage(pageBefore)"
            :title="$t('word.previous')"
        >
            <button type="button" class="pagination--page">
                {{ pageBefore }}
            </button>
        </NuxtLink>

        <button
            type="button"
            class="pagination--page pagination--page__active"
            disabled
        >
            {{ currentIndex }}
        </button>

        <NuxtLink
            v-for="pageAfter in pagesAfter"
            v-show="currentIndex > 2"
            :key="pageAfter"
            :to="replacePage(pageAfter)"
            :title="$t('word.next')"
        >
            <button type="button" class="pagination--page">
                {{ pageAfter }}
            </button>
        </NuxtLink>

        <div
            v-show="
                currentIndex < maxPage - 1 && maxPage - 1 > currentIndex + 1
            "
            class="button pagination--page__hidden"
        >
            …
        </div>

        <NuxtLink
            v-show="currentIndex < maxPage"
            :to="replacePage(maxPage)"
            :title="$t('word.next')"
        >
            <button
                type="button"
                class="pagination--page pagination--last-page"
            >
                {{ maxPage }}
            </button>
        </NuxtLink>

        <NuxtLink
            v-if="currentIndex !== maxPage"
            rel="next"
            :to="replacePage(currentIndex + 1)"
            :title="$t('word.next')"
        >
            <button
                type="button"
                class="pagination--next"
                :disabled="currentIndex === maxPage || total === 0"
            >
                <IconChevronRight is-solid />
            </button>
        </NuxtLink>
    </div>
</template>

<style lang="scss" scoped>
.pagination {
    @apply flex gap-2;

    button,
    .button {
        @apply flex items-center justify-center rounded-full text-xxs md:text-inherit size-8 md:size-10 bg-zinc-100;

        &:disabled:not(.pagination--page__active) {
            @apply cursor-not-allowed text-gray-disabled;
        }
    }

    &--page {
        &:hover:not(:disabled) {
            @apply border border-[#AAE5FF] text-[#005085] bg-white;
        }

        &__active {
            @apply border border-[#0DA2E7] bg-[#F0FBFF] text-[#005085];
        }

        &:disabled {
            @apply cursor-not-allowed;
        }

        &__hidden {
            @apply cursor-default;
        }
    }

    &--prev svg,
    &--next svg {
        @apply size-6;
    }
}
</style>
